import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { duration, easing } from '../motion/tokens';

import { JOURNEY_STAGES } from './content';
import type { JourneyStage } from './content';
import { useIsDesktop, usePrefersReducedMotion } from './hooks';
import { MkContainer, MkEyebrow, MkReveal } from './primitives';
import { ProductMockup } from './ProductMockup';

/**
 * Scroll progress for the pinned story.
 *
 * The pinning itself is `position: sticky` — CSS, not JS — so the browser
 * keeps full control of the scroll. GSAP ScrollTrigger only reads progress and
 * maps it to the active stage, which means the section reads correctly in both
 * directions and never fights native scrolling, momentum or anchor jumps.
 *
 * GSAP is imported dynamically and only on desktop, so tablet and mobile never
 * pay for the bundle.
 */
function useScrollStage(
  trackRef: React.RefObject<HTMLDivElement | null>,
  stageCount: number,
  enabled: boolean,
): { index: number; progress: number } {
  const [state, setState] = useState({ index: 0, progress: 0 });

  useEffect(() => {
    if (!enabled) return;
    const element = trackRef.current;
    if (!element) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    void (async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
      ]);
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);

      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self) => {
          const progress = self.progress;
          // Clamp so the final stage stays selected at progress === 1.
          const index = Math.min(stageCount - 1, Math.floor(progress * stageCount));
          setState((previous) =>
            previous.index === index && Math.abs(previous.progress - progress) < 0.004
              ? previous
              : { index, progress },
          );
        },
      });

      cleanup = () => trigger.kill();
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [trackRef, stageCount, enabled]);

  return state;
}

function StageRail({
  stages,
  activeIndex,
}: {
  stages: JourneyStage[];
  activeIndex: number;
}): JSX.Element {
  return (
    <ol className="flex flex-col gap-1" aria-label="Product story stages">
      {stages.map((stage, index) => {
        const active = index === activeIndex;
        return (
          <li key={stage.id}>
            <div
              className="flex items-center gap-3 py-1.5"
              aria-current={active ? 'step' : undefined}
            >
              <span
                className={`h-px flex-none transition-all duration-500 ${
                  active ? 'w-8 bg-[var(--mk-cyan)]' : 'w-4 bg-[var(--mk-line-strong)]'
                }`}
                aria-hidden
              />
              <span
                className={`text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-500 ${
                  active ? 'text-[var(--mk-ink)]' : 'text-[var(--mk-ink-muted)]'
                }`}
              >
                {stage.label}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Desktop: sticky viewport, copy on the left, product demo on the right. */
function PinnedStory(): JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null);
  const { index, progress } = useScrollStage(trackRef, JOURNEY_STAGES.length, true);
  const stage = JOURNEY_STAGES[index] ?? JOURNEY_STAGES[0];

  return (
    <div
      ref={trackRef}
      // One viewport of scroll per stage keeps the pacing readable rather than
      // twitchy, and gives each mock-up room to be understood.
      style={{ height: `${JOURNEY_STAGES.length * 100}vh` }}
      className="relative"
    >
      <div className="sticky top-0 flex h-dvh items-center overflow-hidden">
        <MkContainer>
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
            <div className="flex flex-col gap-8">
              <MkEyebrow>How Vaanexa works</MkEyebrow>

              <div className="min-h-[240px]">
                <motion.div
                  key={stage.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: duration.standard, ease: easing.entrance }}
                >
                  <p className="text-sm font-semibold text-[var(--mk-ink-muted)]">
                    Stage {index + 1} of {JOURNEY_STAGES.length}
                  </p>
                  <h3 className="mk-h2 mt-3 text-[var(--mk-ink)]">{stage.title}</h3>
                  <p className="mt-4 max-w-md text-lg leading-relaxed text-[var(--mk-ink-soft)]">
                    {stage.description}
                  </p>
                </motion.div>
              </div>

              <StageRail stages={JOURNEY_STAGES} activeIndex={index} />

              <div
                className="h-px w-full overflow-hidden bg-[var(--mk-line)]"
                role="progressbar"
                aria-label="Product story progress"
                aria-valuenow={Math.round(progress * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full bg-[linear-gradient(90deg,var(--mk-blue),var(--mk-violet),var(--mk-cyan))]"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>

            <div className="relative">
              <div
                className="mk-glow-primary pointer-events-none absolute -inset-x-10 -inset-y-16"
                aria-hidden
              />
              <motion.div
                key={stage.id}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: duration.section, ease: easing.entrance }}
              >
                <ProductMockup stageId={stage.id} active />
              </motion.div>
            </div>
          </div>
        </MkContainer>
      </div>
    </div>
  );
}

/**
 * Tablet and mobile: the same six stages as plain stacked scenes. No scroll
 * scrubbing, no pinning — each scene simply reveals as it arrives, and reads
 * fine with motion disabled entirely.
 */
function StackedStory(): JSX.Element {
  return (
    <MkContainer className="flex flex-col gap-16 py-4">
      <MkReveal className="flex flex-col gap-5">
        <MkEyebrow>How Vaanexa works</MkEyebrow>
        <h3 className="mk-h2 text-[var(--mk-ink)]">From first message to closed business.</h3>
      </MkReveal>

      {JOURNEY_STAGES.map((stage, index) => (
        <MkReveal key={stage.id} className="flex flex-col gap-5">
          <p className="text-sm font-semibold text-[var(--mk-ink-muted)]">
            Stage {index + 1} of {JOURNEY_STAGES.length}
          </p>
          <h4 className="text-2xl font-bold tracking-[-0.02em] text-[var(--mk-ink)] sm:text-3xl">
            {stage.title}
          </h4>
          <p className="text-base leading-relaxed text-[var(--mk-ink-soft)]">{stage.description}</p>
          <ProductMockup stageId={stage.id} active={false} allowVideo={false} />
        </MkReveal>
      ))}
    </MkContainer>
  );
}

export function ProductJourney(): JSX.Element {
  const isDesktop = useIsDesktop();
  const reduced = usePrefersReducedMotion();

  // Reduced motion drops the scrubbed experience along with mobile — the
  // stacked version carries identical content.
  const pinned = isDesktop && !reduced;

  return (
    <section id="product" className="relative scroll-mt-24 py-24 sm:py-28">
      <div className="pointer-events-none absolute inset-0 mk-grid" aria-hidden />
      <h2 className="sr-only">How Vaanexa works</h2>
      <div className="relative">{pinned ? <PinnedStory /> : <StackedStory />}</div>
    </section>
  );
}
